import { Button, Group, Modal, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { CornerButton, toneColor, type StatusTone } from "./primitives";

/* ------------------------------------------------------------------ */
/* toast                                                               */
/* ------------------------------------------------------------------ */

/**
 * Bottom-right toast. Describes what happened or was requested, never an
 * unconfirmed result. Auto-dismisses after 4.2 s.
 */
export function toast(title: string, body?: ReactNode, tone: StatusTone = "muted") {
  notifications.show({
    title,
    message: body ?? "",
    color: toneColor[tone],
    position: "bottom-right",
    autoClose: 4200,
    withCloseButton: false,
  });
}

/* ------------------------------------------------------------------ */
/* confirm                                                             */
/* ------------------------------------------------------------------ */

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  /** Button verb that names the action, e.g. "Delete emulator". */
  verb: string;
  danger?: boolean;
  /** High-blast-radius actions: user must type this exact value. */
  typeToConfirm?: string;
  onConfirm: () => void | Promise<unknown>;
};

let current: ConfirmOptions | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Opens the global confirm dialog. One pattern, scaled to blast radius. */
export function confirm(opts: ConfirmOptions) {
  current = opts;
  emit();
}

const close = () => {
  current = null;
  emit();
};

/** Mount once near the app root. */
export function ConfirmHost() {
  const opts = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
  return opts ? <ConfirmDialog key={opts.title + opts.verb} {...opts} /> : null;
}

function ConfirmDialog({ title, body, verb, danger, typeToConfirm, onConfirm }: ConfirmOptions) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = !typeToConfirm || typed === typeToConfirm;

  const run = async () => {
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={close} title={title} size={460} centered>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {body && (
          <Text size="sm" c="var(--na-fg-dimmed)">
            {body}
          </Text>
        )}
        {typeToConfirm && (
          <TextInput
            data-autofocus
            label={
              <span>
                Type <code className="na-mono">{typeToConfirm}</code> to confirm
              </span>
            }
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            autoComplete="off"
            spellCheck={false}
            styles={{ input: { fontFamily: "var(--na-font-mono)" } }}
          />
        )}
        {error && (
          <Text size="xs" c="var(--na-danger-text)">
            {error}
          </Text>
        )}
        <Group justify="flex-end" gap={8}>
          <Button variant="default" onClick={close} h={32}>
            Cancel
          </Button>
          <CornerButton
            plain
            type="submit"
            size="sm"
            danger={danger}
            disabled={busy}
            style={{ opacity: ok ? undefined : 0.4 }}
            aria-disabled={!ok}
          >
            {verb}
          </CornerButton>
        </Group>
      </form>
    </Modal>
  );
}
